'use strict';
/**
 * <sticky-filter> — direction-aware "follow scroll" sidebar for the vertical
 * collection / search filter.
 *
 * Problem it solves: a plain `position: sticky; top: …` sidebar pins its TOP, so
 * when the filter list is taller than the viewport its lower options stay
 * off-screen the whole time the (200-300 item) product grid scrolls past.
 *
 * Behaviour implemented instead:
 *   • scroll DOWN  → the sidebar scrolls up with the page until its BOTTOM edge
 *                    meets the viewport bottom, then it pins to the bottom.
 *   • scroll UP    → the sidebar scrolls down until its TOP edge meets the top
 *                    (just under the sticky header), then it pins to the top.
 *   • fits viewport → behaves like a classic top-pinned sticky.
 *
 * Implementation: native `position: sticky` with a direction-clamped dynamic
 * `top`. The browser composites the pin in the SAME frame as the scroll, so a
 * pinned sidebar never lags. JS only nudges `top` while the sidebar is
 * *transitioning* between the two pins; once pinned, `top` hits a clamp bound and
 * stops changing — the stuck state is 100% native, i.e. jitter-free. (An earlier
 * version drove this with a per-frame `transform`, which shook because the
 * main-thread transform lagged the scroll paint by one frame.)
 *
 * Progressive enhancement: the element keeps its `sticky-1025 top-30` classes, so
 * without this script it falls back to plain top-sticky. `sticky-filter{display:block}`
 * (collection-page.css) keeps layout intact before upgrade.
 *
 * Desktop only (>= 1025px). On smaller screens the filter is an off-canvas drawer
 * and this controller stays inert.
 */
(function () {
  const DESKTOP_MIN = 1025; // matches the theme's *-1025 breakpoint
  const TOP_GAP = 30; // breathing room below the header when top-pinned (px)
  const BOTTOM_GAP = 30; // breathing room above the viewport bottom (px)

  class StickyFilter extends HTMLElement {
    constructor() {
      super();
      this._stickyTop = TOP_GAP; // current applied `top` value (px)
      this._lastScroll = 0;
      this._ticking = false;
      this._enabled = false;
      this._ro = null;

      // Cached layout reads, refreshed off the scroll path to avoid thrash.
      this._sidebarH = 0;
      this._vh = 0;
      this._headerH = 0;

      // Stable, bound references so add/removeEventListener pair up.
      this._requestUpdate = this._requestUpdate.bind(this);
      this._frame = this._update.bind(this);
      this._handleResize = this._handleResize.bind(this);
      this._handleMutation = this._handleMutation.bind(this);
    }

    connectedCallback() {
      this._lastScroll = window.scrollY;
      this._measure();
      this._evaluate();

      window.addEventListener('scroll', this._requestUpdate, { passive: true });
      window.addEventListener('resize', this._handleResize, { passive: true });

      if ('ResizeObserver' in window) {
        // Recompute when the sidebar changes height (accordions, lazy images).
        this._ro = new ResizeObserver(this._handleMutation);
        this._ro.observe(this);
      }

      this._requestUpdate();
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this._requestUpdate);
      window.removeEventListener('resize', this._handleResize);
      if (this._ro) {
        this._ro.disconnect();
        this._ro = null;
      }
    }

    /** Cache the measurements that force layout, so the scroll handler stays cheap. */
    _measure() {
      this._sidebarH = this.offsetHeight;
      this._vh = window.innerHeight;
      const inner = document.getElementById('header-sticky');
      this._headerH = inner ? inner.offsetHeight : 0;
    }

    _handleMutation() {
      this._measure();
      this._requestUpdate();
    }

    _handleResize() {
      this._measure();
      this._evaluate();
      this._requestUpdate();
    }

    /** Enable on desktop only; reset cleanly when leaving desktop widths. */
    _evaluate() {
      const shouldEnable = window.innerWidth >= DESKTOP_MIN;
      if (shouldEnable === this._enabled) return;
      this._enabled = shouldEnable;

      if (shouldEnable) {
        this._lastScroll = window.scrollY;
        this._stickyTop = this._topOffset();
        this.style.position = 'sticky';
        this.style.top = this._stickyTop + 'px';
      } else {
        this.style.position = '';
        this.style.top = '';
      }
    }

    _requestUpdate() {
      if (this._ticking) return;
      this._ticking = true;
      requestAnimationFrame(this._frame);
    }

    /** Top-pin position: clears a currently visible sticky header (cheap, no layout). */
    _topOffset() {
      const stuckHeader = document.querySelector(
        '.section-header.shopify-section-header-sticky:not(.shopify-section-header-hidden)'
      );
      return (stuckHeader ? this._headerH : 0) + TOP_GAP;
    }

    _update() {
      this._ticking = false;
      if (!this._enabled) return;

      const scrollY = window.scrollY;
      const dir = scrollY - this._lastScroll; // > 0 down, < 0 up
      this._lastScroll = scrollY;

      const maxTop = this._topOffset(); // top-pin value
      let next;

      if (this._sidebarH + maxTop + BOTTOM_GAP <= this._vh) {
        // Sidebar fits → plain top pin, nothing to chase.
        next = maxTop;
      } else {
        // Taller than the viewport: drift `top` toward the bottom pin while
        // scrolling down and the top pin while scrolling up. The clamp ends the
        // drift, after which native sticky holds the pin with no further writes.
        const minTop = this._vh - this._sidebarH - BOTTOM_GAP; // bottom-pin value (negative)
        next = this._stickyTop - dir;
        if (next < minTop) next = minTop;
        else if (next > maxTop) next = maxTop;
      }

      if (next !== this._stickyTop) {
        this._stickyTop = next;
        this.style.top = next + 'px';
      }
    }
  }

  if (!customElements.get('sticky-filter')) {
    customElements.define('sticky-filter', StickyFilter);
  }
})();
